import React, { memo, useEffect, useMemo, useState } from 'react';
import { getCookieId } from '../../../../WebserverUser';
import { serviceRequest } from '../../../../../shared/xomics-webserver-client';
import './DrugMol.css';

function structureDocument(structure, isLightTheme) {
  if (!structure?.pdbUrl) return null;
  const background = isLightTheme ? '#ffffff' : '#2e2e2e';
  const text = isLightTheme ? '#1f2937' : '#ffffff';
  const positions = (structure.mutations || [])
    .map((mutation) => Number(String(mutation).match(/[A-Za-z*](\d+)[A-Za-z*]/)?.[1]))
    .filter((position) => Number.isSafeInteger(position) && position > 0);
  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <script src="https://cdnjs.cloudflare.com/ajax/libs/3Dmol/2.0.1/3Dmol-min.js"></script>
  <style>
    html,body,#viewer{width:100%;height:100%;margin:0;overflow:hidden;background:${background}}
    body{font-family:Arial,sans-serif;color:${text}}
    .controls{position:absolute;z-index:2;top:12px;left:12px;display:flex;gap:7px}
    button{border:1px solid rgba(148,163,184,.35);border-radius:999px;padding:7px 11px;background:${isLightTheme ? 'rgba(255,255,255,.92)' : 'rgba(25,30,36,.88)'};color:${text};cursor:pointer}
  </style>
</head>
<body>
  <div class="controls"><button onclick="viewer.zoomTo();viewer.render()">Reset</button><button onclick="toggleSpin()">Rotate</button></div>
  <div id="viewer"></div>
  <script>
    var viewer = $3Dmol.createViewer('viewer', {backgroundColor:${JSON.stringify(background)},preserveDrawingBuffer:true});
    var spinning = false;
    function toggleSpin(){spinning=!spinning;viewer.spin(spinning);}
    fetch(${JSON.stringify(structure.pdbUrl)})
      .then(function(response){if(!response.ok)throw new Error('PDB '+response.status);return response.text();})
      .then(function(pdb){
        viewer.addModel(pdb,'pdb');
        viewer.setStyle({}, {cartoon:{color:'spectrum'}});
        viewer.addStyle({hetflag:true}, {stick:{colorscheme:'greenCarbon',radius:.22},sphere:{scale:.3}});
        ${positions.map((position) => `viewer.addStyle({resi:${position}}, {stick:{color:'#ef4444',radius:.25},sphere:{color:'#ef4444',scale:.35}});`).join('\n        ')}
        viewer.zoomTo();viewer.render();
      })
      .catch(function(error){document.getElementById('viewer').textContent='Structure could not be loaded: '+error.message;});
  </script>
</body>
</html>`;
}

const DrugMol = memo(({ variantIdentity, therapeuticImplications }) => {
  const gene = variantIdentity?.gene;
  const alteration = variantIdentity?.alteration;
  const [isLightTheme, setIsLightTheme] = useState(false);
  const [selectedItemJSON, setSelectedItemJSON] = useState('');
  const [structure, setStructure] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const rankedEvidenceItems = useMemo(() => {
    const rows = [];
    const sourceByDrug = new Map();
    const tiers = {
      FDA: { text: 'FDA Approved', score: 20 },
      GUIDELINE: { text: 'Guideline', score: 15 },
      CLINICAL: { text: 'Clinical', score: 10 },
      PRECLINICAL: { text: 'Pre-clinical', score: 5 },
    };
    const add = (row) => {
      rows.push(row);
      if (!sourceByDrug.has(row.drugName)) sourceByDrug.set(row.drugName, new Set());
      sourceByDrug.get(row.drugName).add(row.source);
    };
    (therapeuticImplications?.oncokb_fda_approved_treatments || []).forEach((item, index) => {
      const level = item.level || item.evidenceLevel;
      const tier = level === 'LEVEL_1' ? tiers.FDA
        : ['LEVEL_2', 'LEVEL_R1'].includes(level) ? tiers.GUIDELINE : null;
      if (item.drugs && tier) add({
        id: `onco-${index}`,
        drugName: item.drugs,
        cancerType: item.cancerType || 'Not specified',
        source: 'OncoKB',
        level,
        tier,
      });
    });
    (therapeuticImplications?.civic_literature_evidence || []).forEach((item, index) => {
      const drugName = Array.isArray(item.drugs) ? item.drugs.join(' + ') : item.drugs;
      const tier = ['A', 'B'].includes(item.evidence_level) ? tiers.CLINICAL
        : ['C', 'D', 'E'].includes(item.evidence_level) ? tiers.PRECLINICAL : null;
      if (drugName && tier) add({
        id: `civic-${index}`,
        drugName,
        cancerType: item.disease || 'Not specified',
        source: 'CIViC',
        level: item.evidence_level,
        tier,
      });
    });
    return rows
      .filter((item) => {
        const sources = sourceByDrug.get(item.drugName);
        return !(sources.size === 1 && sources.has('CIViC'));
      })
      .sort((left, right) => {
        const leftCombination = left.drugName.includes('+');
        const rightCombination = right.drugName.includes('+');
        if (leftCombination !== rightCombination) return leftCombination ? 1 : -1;
        return right.tier.score - left.tier.score || left.drugName.localeCompare(right.drugName);
      });
  }, [therapeuticImplications]);

  useEffect(() => {
    const readTheme = () => setIsLightTheme(
      document.documentElement.classList.contains('ocrp-theme-light'),
    );
    readTheme();
    const handleTheme = (event) => setIsLightTheme(event.detail?.theme === 'light');
    window.addEventListener('ocrp-theme-change', handleTheme);
    return () => window.removeEventListener('ocrp-theme-change', handleTheme);
  }, []);

  useEffect(() => {
    setSelectedItemJSON(rankedEvidenceItems[0] ? JSON.stringify(rankedEvidenceItems[0]) : '');
    setStructure(null);
    setStatus(rankedEvidenceItems.length ? 'loading' : 'idle');
    setError(null);
  }, [rankedEvidenceItems]);

  useEffect(() => {
    if (!selectedItemJSON || !gene) return undefined;
    const controller = new AbortController();
    const selected = JSON.parse(selectedItemJSON);
    setStatus('loading');
    setError(null);
    setStructure(null);
    serviceRequest('oncorisk', getCookieId(), '/drug-structure', {
      method: 'POST',
      signal: controller.signal,
      body: JSON.stringify({
        gene,
        drug: selected.drugName,
        mutations: alteration ? [alteration] : [],
      }),
    }).then((result) => {
      setStructure(result);
      setStatus('ready');
    }).catch((requestError) => {
      if (requestError.name === 'AbortError') return;
      setError(requestError.message);
      setStatus('error');
    });
    return () => controller.abort();
  }, [alteration, gene, selectedItemJSON]);

  const selectedItem = useMemo(
    () => selectedItemJSON ? JSON.parse(selectedItemJSON) : null,
    [selectedItemJSON],
  );
  const htmlContent = useMemo(
    () => structureDocument(structure, isLightTheme),
    [isLightTheme, structure],
  );

  return (
    <div className="ONCRP-TOOLS-QP-span-col-6 ONCRP-TOOLS-QP-span-row-4">
      <div className="ONCRP-TOOLS-QP-card">
        <div className="ONCRP-TOOLS-QP-drugmol-header">
          <i className="fa fa-cubes"></i><h3>Structure</h3>
          {gene && <span className="ONCRP-TOOLS-QP-drugmol-gene-chip">{gene}</span>}
        </div>
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <div className="ONCRP-TOOLS-QP-ngl-viewer-container" style={{ flex: 2, borderRight: '1px solid rgba(255,255,255,.1)', position: 'relative' }}>
            {status === 'loading' && <div className="ONCRP-TOOLS-QP-drugmol-placeholder">Loading 3D structure...</div>}
            {status === 'error' && <div className="ONCRP-TOOLS-QP-drugmol-placeholder error">{error || 'Structure unavailable'}</div>}
            {status === 'idle' && <div className="ONCRP-TOOLS-QP-drugmol-placeholder">No high-quality drug evidence found.</div>}
            {status === 'ready' && htmlContent && (
              <iframe
                srcDoc={htmlContent}
                title={`3D visualization of ${gene} with ${selectedItem?.drugName}`}
                sandbox="allow-scripts"
                style={{ width: '100%', height: '100%', border: 0 }}
              />
            )}
          </div>
          <div className="ONCRP-TOOLS-QP-drugmol-details-panel">
            <div className="ONCRP-TOOLS-QP-drugmol-controls">
              <label>Therapeutic evidence</label>
              <select value={selectedItemJSON} onChange={(event) => setSelectedItemJSON(event.target.value)} disabled={!rankedEvidenceItems.length || status === 'loading'}>
                <option value="">Select evidence</option>
                {rankedEvidenceItems.map((item) => (
                  <option key={item.id} value={JSON.stringify(item)}>{item.drugName} ({item.cancerType})</option>
                ))}
              </select>
            </div>
            <div className="ONCRP-TOOLS-QP-drugmol-scrollable-content">
              {selectedItem && (
                <div className="ONCRP-TOOLS-QP-drugmol-infobox">
                  <div className="ONCRP-TOOLS-QP-info-item"><span className="label">Drug</span><span className="value">{selectedItem.drugName}</span></div>
                  <div className="ONCRP-TOOLS-QP-info-item"><span className="label">Cancer type</span><span className="value">{selectedItem.cancerType}</span></div>
                  <div className="ONCRP-TOOLS-QP-info-item"><span className="label">Source</span><span className="value">{selectedItem.source}</span></div>
                  <div className="ONCRP-TOOLS-QP-info-item"><span className="label">Tier</span><span className="value">{selectedItem.tier.text}</span></div>
                </div>
              )}
              {structure?.metadata && (
                <div className="ONCRP-TOOLS-QP-drugmol-infobox secondary">
                  {structure.metadata.title && <div className="ONCRP-TOOLS-QP-info-item-multiline"><span className="label">Structure</span><span className="value sub-item">{structure.metadata.title}</span></div>}
                  {structure.metadata.classification && <div className="ONCRP-TOOLS-QP-info-item"><span className="label">Classification</span><span className="value">{structure.metadata.classification}</span></div>}
                  {structure.metadata.resolution && <div className="ONCRP-TOOLS-QP-info-item"><span className="label">Resolution</span><span className="value">{structure.metadata.resolution} Å</span></div>}
                  {structure.metadata.experimentalMethod?.length > 0 && <div className="ONCRP-TOOLS-QP-info-item"><span className="label">Method</span><span className="value">{structure.metadata.experimentalMethod.join(', ')}</span></div>}
                </div>
              )}
            </div>
            <div className="ONCRP-TOOLS-QP-drugmol-footer">
              <div>{structure?.pdbId ? `Source: RCSB PDB ${structure.pdbId}` : 'Select evidence to begin'}</div>
              {alteration && structure && <div style={{ fontSize: '.85em', color: '#26de81' }}>{alteration} highlighted</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default DrugMol;
